import { doc, updateDoc, arrayUnion, arrayRemove, deleteField } from 'firebase/firestore';
import { firestore } from './firebase';

export const MAX_REGISTERED_DEVICES = 2;

// A user-defined tag: a free-text label, optionally given a colour. The user's
// global tag vocabulary, reused across packs. Sound→tag assignments live
// per-pack in DatabaseUserPackData (keyed by sound uid → these tag ids).
export type UserTag = {
    id: string;
    name: string;
    colorId: string | null; // references a TagColor.id, or null when uncoloured
}

export type SubscriptionInterval = 'month' | 'year';

export type DatabaseUser = {
    createdAt: any; // Firestore Timestamp type (can refine if you import Timestamp)
    email: string;
    subscription: {
        id: string;
        status: string;
        cancelAtPeriodEnd: boolean;
        currentPeriodEnd: number | null; // UNIX seconds
        // Billing cadence, from the Stripe price. null on subscriptions written
        // before the yearly plan existed (all of which are monthly).
        interval: SubscriptionInterval | null;
    } | null;
    ownedSoundPacks: string[];
    cartItems: string[]; // pack IDs of items in the cart
    registeredDevices: { [deviceId: string]: string }; // device id → display name
    userTags: UserTag[]; // the user's global, reusable tag definitions
    // Pack ids the user has already acknowledged. A pack is shown with a "new"
    // badge until its id lands here (added when its info page is opened).
    // `undefined` means never seeded — the plugin seeds it with all current
    // pack ids on first open so existing packs aren't all flagged.
    seenPackIds?: string[];
    // ISO timestamp of the user's last visit to the plugin's News tab. Non-pack
    // news cards created after it show a "New" badge on the next visit.
    // `undefined` means never seeded — the plugin sets it to "now" on first open.
    newsLastSeenAt?: string;
}

function parseUserTags(value: unknown): UserTag[] {
    if (!Array.isArray(value)) return [];
    return value.flatMap((entry): UserTag[] => {
        if (typeof entry !== 'object' || !entry || typeof (entry as any).id !== 'string' || typeof (entry as any).name !== 'string') {
            return [];
        }
        const colorId = (entry as any).colorId;
        return [{ id: (entry as any).id, name: (entry as any).name, colorId: typeof colorId === 'string' ? colorId : null }];
    });
}

// Field name constants (kept private to this file)
class FieldPaths {
    static readonly EMAIL = "email";
    static readonly CREATED_AT = "createdAt";
    static readonly SUBSCRIPTION = "subscription";
    // Wire name intentionally kept as "ownedSamplePacks" (Sound rename) — shared with
    // Cloud Functions and existing production user docs; do not change the string value.
    static readonly OWNED_SOUND_PACKS = "ownedSamplePacks";
    static readonly CART_ITEMS = "cartItems";
    static readonly REGISTERED_DEVICES = "registeredDevices";
    static readonly USER_TAGS = "userTags";
    static readonly SEEN_PACK_IDS = "seenPackIds";
    static readonly NEWS_LAST_SEEN_AT = "newsLastSeenAt";
}

export class DatabaseUserModel {
    static fromFirestore(data: any): DatabaseUser {
        if (
            typeof data !== "object" ||
            !data ||
            typeof data[FieldPaths.EMAIL] !== "string" ||
            !data[FieldPaths.CREATED_AT]
        ) {
            throw new Error("Invalid user document structure");
        }
        return {
            email: data[FieldPaths.EMAIL],
            createdAt: data[FieldPaths.CREATED_AT],
            subscription: data[FieldPaths.SUBSCRIPTION] && typeof data[FieldPaths.SUBSCRIPTION] === 'object' ? {
                id: data[FieldPaths.SUBSCRIPTION].id,
                status: data[FieldPaths.SUBSCRIPTION].status,
                cancelAtPeriodEnd: data[FieldPaths.SUBSCRIPTION].cancelAtPeriodEnd ?? false,
                currentPeriodEnd: typeof data[FieldPaths.SUBSCRIPTION].currentPeriodEnd === 'number' ? data[FieldPaths.SUBSCRIPTION].currentPeriodEnd : null,
                interval: data[FieldPaths.SUBSCRIPTION].interval === 'year' || data[FieldPaths.SUBSCRIPTION].interval === 'month' ? data[FieldPaths.SUBSCRIPTION].interval : null,
            } : null,
            ownedSoundPacks: Array.isArray(data[FieldPaths.OWNED_SOUND_PACKS]) ? data[FieldPaths.OWNED_SOUND_PACKS] : [],
            cartItems: Array.isArray(data[FieldPaths.CART_ITEMS]) ? data[FieldPaths.CART_ITEMS] : [],
            registeredDevices: data[FieldPaths.REGISTERED_DEVICES] && typeof data[FieldPaths.REGISTERED_DEVICES] === 'object'
                ? data[FieldPaths.REGISTERED_DEVICES]
                : {},
            userTags: parseUserTags(data[FieldPaths.USER_TAGS]),
            seenPackIds: Array.isArray(data[FieldPaths.SEEN_PACK_IDS]) ? data[FieldPaths.SEEN_PACK_IDS] : undefined,
            newsLastSeenAt: typeof data[FieldPaths.NEWS_LAST_SEEN_AT] === 'string' ? data[FieldPaths.NEWS_LAST_SEEN_AT] : undefined,
        };
    }

    static toFirestore(user: DatabaseUser): any {
        return {
            [FieldPaths.EMAIL]: user.email,
            [FieldPaths.CREATED_AT]: user.createdAt,
            [FieldPaths.SUBSCRIPTION]: user.subscription ? {
                id: user.subscription.id,
                status: user.subscription.status,
                cancelAtPeriodEnd: user.subscription.cancelAtPeriodEnd,
                currentPeriodEnd: user.subscription.currentPeriodEnd,
                interval: user.subscription.interval,
            } : null,
            [FieldPaths.OWNED_SOUND_PACKS]: user.ownedSoundPacks,
            [FieldPaths.CART_ITEMS]: user.cartItems,
            [FieldPaths.REGISTERED_DEVICES]: user.registeredDevices ?? {},
            [FieldPaths.USER_TAGS]: user.userTags ?? [],
            ...(user.seenPackIds !== undefined ? { [FieldPaths.SEEN_PACK_IDS]: user.seenPackIds } : {}),
            ...(user.newsLastSeenAt !== undefined ? { [FieldPaths.NEWS_LAST_SEEN_AT]: user.newsLastSeenAt } : {}),
        };
    }

    static async registerDevice(userId: string, deviceId: string, deviceName: string): Promise<void> {
        const userRef = doc(firestore, 'users', userId);
        await updateDoc(userRef, {
            [`${FieldPaths.REGISTERED_DEVICES}.${deviceId}`]: deviceName
        });
    }

    /**
     * Swap one registered device for another in a single atomic write: removes
     * `deviceIdToRemove` and registers `newDeviceId` together, so the device
     * count never dips below the limit mid-swap (otherwise a contending device
     * could grab the freed slot). Used when the limit is reached and the user
     * picks an existing device to replace with the current one.
     */
    static async replaceDevice(
        userId: string,
        deviceIdToRemove: string,
        newDeviceId: string,
        newDeviceName: string,
    ): Promise<void> {
        const userRef = doc(firestore, 'users', userId);
        await updateDoc(userRef, {
            [`${FieldPaths.REGISTERED_DEVICES}.${deviceIdToRemove}`]: deleteField(),
            [`${FieldPaths.REGISTERED_DEVICES}.${newDeviceId}`]: newDeviceName,
        });
    }

    static async resetOwnedSoundPacks(userId: string): Promise<void> {
        const userRef = doc(firestore, 'users', userId);
        await updateDoc(userRef, {
            [FieldPaths.OWNED_SOUND_PACKS]: []
        });
    }

    static async addToCart(userId: string, packId: string): Promise<void> {
        const userRef = doc(firestore, 'users', userId);
        await updateDoc(userRef, {
            [FieldPaths.CART_ITEMS]: arrayUnion(packId)
        });
    }

    static async removeFromCart(userId: string, packId: string): Promise<void> {
        const userRef = doc(firestore, 'users', userId);
        await updateDoc(userRef, {
            [FieldPaths.CART_ITEMS]: arrayRemove(packId)
        });
    }

    static async clearCart(userId: string): Promise<void> {
        const userRef = doc(firestore, 'users', userId);
        await updateDoc(userRef, {
            [FieldPaths.CART_ITEMS]: []
        });
    }

    /**
     * Overwrites the user's global tag definitions. Tag edits (add/rename/delete/
     * recolour) are managed as a whole array by the UI, so a single replace keeps
     * the write path simple and atomic.
     */
    static async setUserTags(userId: string, userTags: UserTag[]): Promise<void> {
        const userRef = doc(firestore, 'users', userId);
        await updateDoc(userRef, {
            [FieldPaths.USER_TAGS]: userTags.map(t => ({ id: t.id, name: t.name, colorId: t.colorId ?? null }))
        });
    }

    /**
     * Establish the "new pack" baseline: mark every currently-existing pack as
     * already seen. Called once when the field is absent (first load) so existing
     * packs don't all show a "new" badge — only packs created afterwards will.
     * (Driven by the plugin gallery; mirrored here for model parity.)
     */
    static async seedSeenPacks(userId: string, packIds: string[]): Promise<void> {
        const userRef = doc(firestore, 'users', userId);
        await updateDoc(userRef, {
            [FieldPaths.SEEN_PACK_IDS]: packIds,
        });
    }

    /**
     * Acknowledge a pack as seen (clears its "new" badge). Called when the user
     * opens the pack's info page. arrayUnion keeps it idempotent.
     * (Driven by the plugin gallery; mirrored here for model parity.)
     */
    static async markPackSeen(userId: string, packId: string): Promise<void> {
        const userRef = doc(firestore, 'users', userId);
        await updateDoc(userRef, {
            [FieldPaths.SEEN_PACK_IDS]: arrayUnion(packId),
        });
    }

    /**
     * Record a visit to the News tab (or, on first plugin open, seed the
     * baseline). Non-pack news created before this stops reading as new.
     * (Driven by the plugin News tab; mirrored here for model parity.)
     */
    static async markNewsSeen(userId: string, seenAt: string): Promise<void> {
        const userRef = doc(firestore, 'users', userId);
        await updateDoc(userRef, {
            [FieldPaths.NEWS_LAST_SEEN_AT]: seenAt,
        });
    }
}
